// Clock changes (daylight saving). Classes are stored in IST wall-clock
// time, typed once and reused for every date, so when the UK (late October /
// late March) or US (early November / mid March) clocks change, a student
// abroad suddenly has class an hour earlier or later in their own time.
// planClockChange() works out, per upcoming class, how far it must move in
// IST so each student keeps the local time they had when it was booked.

// Minutes ahead of UTC for a time zone at an instant (London in winter 0,
// New York in winter -300, India 330).
export function utcOffsetMinutes(tz, instant) {
  const name = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' })
    .formatToParts(instant).find(p => p.type === 'timeZoneName')?.value || 'GMT'
  const m = name.match(/GMT([+-])(\d{2}):?(\d{2})?/)
  return m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] || 0)) : 0
}

const istInstant = (date, time) => new Date(`${date}T${(time || '00:00').slice(0, 5)}:00+05:30`)
const istParts = instant => {
  const s = new Date(instant.getTime() + 330 * 60000).toISOString()
  return { date: s.slice(0, 10), time: s.slice(11, 16) + ':00' }
}

// cls: { id, class_date, start_time, created_at }; timezones: the enrolled
// students' time zones. Returns { action: 'move', minutes, newDate, newTime }
// or { action: 'skip', reason }.
export function planClockChange(cls, timezones) {
  if (!cls.start_time) return { action: 'skip', reason: 'No start time' }
  if (!cls.created_at) return { action: 'skip', reason: 'Booking date unknown' }
  const zones = [...new Set(timezones.filter(Boolean))]
  if (!zones.length) return { action: 'skip', reason: 'Student time zone not set' }
  const start = istInstant(cls.class_date, cls.start_time)
  const booked = new Date(cls.created_at)
  const shifts = zones.map(tz => utcOffsetMinutes(tz, booked) - utcOffsetMinutes(tz, start))
  if (shifts.every(s => s === 0)) return { action: 'skip', reason: 'No clock change since booking' }
  if (new Set(shifts).size > 1) return { action: 'skip', reason: 'Students in different time zones: decide by hand' }
  return shiftPlan(cls, shifts[0])
}

// A move of `minutes` in IST, e.g. to make room for another student's class.
export function shiftPlan(cls, minutes) {
  const moved = istParts(new Date(istInstant(cls.class_date, cls.start_time).getTime() + minutes * 60000))
  return { action: 'move', minutes, newDate: moved.date, newTime: moved.time }
}

// Guards against moving a class twice: true when the class already sits at
// the local time the student had before the clock change. pastSlots: the
// same teacher and student's earlier classes ({ class_date, start_time }).
export function alreadyAtUsualTime(cls, tz, pastSlots) {
  const booked = utcOffsetMinutes(tz, new Date(cls.created_at))
  const local = instant => new Intl.DateTimeFormat('en-GB', { timeZone: tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(instant)
  const now = local(istInstant(cls.class_date, cls.start_time))
  return pastSlots.some(p => {
    const at = istInstant(p.class_date, p.start_time)
    return utcOffsetMinutes(tz, at) === booked && local(at) === now
  })
}

// Marks moves that would overlap another class of the same teacher (using
// every class's time after the moves). Pairs that already overlapped before
// any move (siblings taught together, existing double bookings) are not new
// clashes and are ignored. rows: [{ cls, plan }]. Returns id -> { cls, date, time }.
export function findTeacherClashes(rows, defaultMinutes = 60) {
  const span = (r, date, time) => {
    const s = istInstant(date, time).getTime()
    return [s, s + (r.cls.duration_minutes || defaultMinutes) * 60000]
  }
  const after = r => r.plan.action === 'move' ? [r.plan.newDate, r.plan.newTime] : [r.cls.class_date, r.cls.start_time]
  const overlap = ([s1, e1], [s2, e2]) => s1 < e2 && s2 < e1
  const clashes = new Map()
  for (const a of rows) {
    if (a.plan.action !== 'move' || !a.cls.teacher_id) continue
    const aAfter = span(a, ...after(a))
    const aBefore = span(a, a.cls.class_date, a.cls.start_time)
    const other = rows.find(b => b !== a && b.cls.teacher_id === a.cls.teacher_id && b.cls.start_time &&
      overlap(aAfter, span(b, ...after(b))) && !overlap(aBefore, span(b, b.cls.class_date, b.cls.start_time)))
    if (other) { const [date, time] = after(other); clashes.set(a.cls.id, { cls: other.cls, date, time }) }
  }
  return clashes
}
